/**
 * Env-gated ad-pixel injectors.
 *
 * Both components no-op when their key is unset (dev / pre-launch / privacy
 * teardown) and inject exactly one script tag on first mount. Re-mounts and
 * route changes do not duplicate the tag — guarded by a `data-` attribute
 * selector on `document.head`.
 *
 * Helpers (`fireMetaConversion`, `fireGoogleAdsConversion`) call the
 * provider's globals if and only if the script has loaded. They silently
 * no-op otherwise — callers don't have to know whether the pixel is on.
 */

import { useEffect } from "react";

const META_TAG_ATTR = "data-viz-meta-pixel";
const GOOGLE_TAG_ATTR = "data-viz-google-ads";

interface MetaPixelProps {
  pixelId?: string;
}

export function MetaPixel({ pixelId }: MetaPixelProps): null {
  useEffect(() => {
    if (!pixelId) return;
    if (typeof document === "undefined") return;
    if (document.head.querySelector(`script[${META_TAG_ATTR}]`)) return;

    const safeId = JSON.stringify(pixelId);
    const script = document.createElement("script");
    script.setAttribute(META_TAG_ATTR, "init");
    script.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init',${safeId});fbq('track','PageView');`;
    document.head.appendChild(script);
  }, [pixelId]);

  return null;
}

interface GoogleAdsTagProps {
  tagId?: string;
}

export function GoogleAdsTag({ tagId }: GoogleAdsTagProps): null {
  useEffect(() => {
    if (!tagId) return;
    if (typeof document === "undefined") return;
    if (document.head.querySelector(`script[${GOOGLE_TAG_ATTR}="loader"]`)) return;

    const loader = document.createElement("script");
    loader.setAttribute(GOOGLE_TAG_ATTR, "loader");
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`;
    document.head.appendChild(loader);

    const init = document.createElement("script");
    init.setAttribute(GOOGLE_TAG_ATTR, "init");
    const safeId = JSON.stringify(tagId);
    init.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config',${safeId});`;
    document.head.appendChild(init);
  }, [tagId]);

  return null;
}

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
  }
}

export function fireMetaConversion(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  const fbq = window.fbq;
  if (typeof fbq !== "function") return;
  if (params) fbq("track", eventName, params);
  else fbq("track", eventName);
}

export function fireGoogleAdsConversion(
  sendTo: string | undefined,
  params?: Record<string, unknown>,
): void {
  if (!sendTo) return;
  if (typeof window === "undefined") return;
  const gtag = window.gtag;
  if (typeof gtag !== "function") return;
  gtag("event", "conversion", { send_to: sendTo, ...(params ?? {}) });
}
